import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Language } from '@/i18n';

/** Fusionne des classes Tailwind sans conflit. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Selectionne la variante linguistique d'un champ bilingue.
 * Picks the language variant of a bilingual field.
 */
export function pickLocale<T extends object>(
  source: T | null | undefined,
  key: string,
  language: Language,
): string {
  if (!source) return '';
  const record = source as Record<string, unknown>;
  const value = record[language === 'en' ? `${key}En` : `${key}Fr`];
  if (typeof value === 'string' && value.length > 0) return value;
  const fallback = record[`${key}Fr`];
  return typeof fallback === 'string' ? fallback : '';
}

export function formatDate(value: string | Date | null | undefined, language: Language): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(value: string | Date | null | undefined, language: Language): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatNumber(value: number | null | undefined, language: Language, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatPercent(value: number | null | undefined, language: Language, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${formatNumber(value, language, digits)} %`;
}

/** Convertit une duree en secondes vers un libelle court (2 h 15 min). */
export function formatDuration(seconds: number | null | undefined, language: Language): string {
  if (!seconds || seconds <= 0) return language === 'fr' ? '0 min' : '0 min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} h`;
  return `${hours} h ${minutes} min`;
}

export function formatMinutes(minutes: number | null | undefined, language: Language): string {
  return formatDuration((minutes ?? 0) * 60, language);
}

export function initials(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

/** Taille lisible d'un fichier, dans les unites de la langue active. */
export function fileSize(bytes: number | null | undefined, language: Language = 'fr'): string {
  if (!bytes) return '';
  const units = language === 'en' ? ['B', 'KB', 'MB', 'GB'] : ['o', 'Ko', 'Mo', 'Go'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

/** Declenche le telechargement d'un blob cote navigateur. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  // Liberer l'adresse immediatement peut devancer le demarrage du
  // telechargement : le navigateur perd alors le nom, voire le fichier.
  // Revoking at once can outrun the download itself.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
